import { useCallback, useEffect, useState } from 'react';
import {
  DEFAULT_AUTO_CENTER_MODE,
  fetchAutoCenterModePreference,
  saveAutoCenterModePreference,
  type AutoCenterMode,
} from '../lib/autoCenterPreference';

interface UseAutoCenterPreferenceResult {
  autoCenterMode: AutoCenterMode;
  autoCenterPreferenceReady: boolean;
  updateAutoCenterMode: (nextMode: AutoCenterMode) => void;
}

export function useAutoCenterPreference(): UseAutoCenterPreferenceResult {
  const [autoCenterMode, setAutoCenterMode] = useState<AutoCenterMode | null>(null);
  const [autoCenterPreferenceReady, setAutoCenterPreferenceReady] = useState(false);

  useEffect(() => {
    let cancelled = false;

    void fetchAutoCenterModePreference()
      .then((remoteMode) => {
        if (cancelled) {
          return;
        }

        setAutoCenterMode(remoteMode);
        setAutoCenterPreferenceReady(true);
      })
      .catch((err: unknown) => {
        if (cancelled) {
          return;
        }

        console.warn('[autoCenterPreference] failed to load remote preference', err);
        setAutoCenterMode(DEFAULT_AUTO_CENTER_MODE);
        setAutoCenterPreferenceReady(true);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const updateAutoCenterMode = useCallback((nextMode: AutoCenterMode) => {
    setAutoCenterMode(nextMode);

    void saveAutoCenterModePreference(nextMode).catch((err: unknown) => {
      console.warn('[autoCenterPreference] failed to save remote preference', err);
    });
  }, []);

  return {
    autoCenterMode: autoCenterMode ?? 'off',
    autoCenterPreferenceReady,
    updateAutoCenterMode,
  };
}
